import { Component, Input, OnInit } from '@angular/core';
import { IhmNotificationMessage, IhmNotificationGroup } from './interfaces/ihm-notifications.interface';
import * as _ from 'lodash';
import * as momentImported from 'moment';
import { LocalStorageService } from './../../shared/services/localstorage.service';
const moment = momentImported;

@Component({
  selector: 'ihm-notifications',
  templateUrl: './ihm-notifications.component.html',
  styleUrls: ['./ihm-notifications.component.scss']
})
export class IhmNotificationsComponent implements OnInit {
  @Input() notifications: IhmNotificationMessage[];
  public notificationsBadgeLabel: string;
  public notificationMessages: IhmNotificationGroup[];
  readonly DATE_FORMAT_FOR_GROUP_NAME: string = 'YYYY-MM-DD';

  constructor(private localStorageService: LocalStorageService) {}

  ngOnInit(): void {
    this.notificationsBadgeLabel = this.getNotificationsCount();
    this.notificationMessages = this.getNotificationMessages();
  }

  public getNotificationMessages(): IhmNotificationGroup[] {
    // New group to organize all messages by date.
    const notificationGroup: IhmNotificationGroup[] = [];

    // Avoid sorting and grouping an empty array of messages.
    if (!this.notifications || !this.notifications.length) {
      return notificationGroup;
    }

    // Order messages by date.
    this.notifications = _.orderBy(this.notifications, (notification: IhmNotificationMessage) => notification.date, 'desc');
    // Get the read notifications from the storage.
    const readKeys: string[] = JSON.parse(this.localStorageService.getItem(this.localStorageService.notificationDrawerReadKeys));

    // Loop all messages:
    this.notifications.forEach((notification: IhmNotificationMessage) => {
      const nameFromDate: string = moment(notification.date).format(this.DATE_FORMAT_FOR_GROUP_NAME);
      // Check if this message was already grouped.
      const groupExists: boolean =
        notificationGroup.length > 0 && notificationGroup.some((currentGroup: IhmNotificationGroup) => currentGroup.name === nameFromDate);

      // If we didn't find this group name:
      if (!groupExists) {
        // Add this group to the group list.
        notificationGroup.push(this.createNotificationGroup(nameFromDate, notification.date, readKeys));
      }
    });

    // Return the grouped list of messages per day.
    return notificationGroup;
  }

  public markNotificationsAsRead(): void {
    // Get all notifications unique identifiers.
    const allKeys: string[] = this.notifications.map((notification: IhmNotificationMessage) => notification.key);
    // Store the read notifications.
    this.localStorageService.setItem(this.localStorageService.notificationDrawerReadKeys, JSON.stringify(allKeys));
    // Update the notifications badge label.
    this.notificationsBadgeLabel = this.getNotificationsCount();
    this.notificationMessages = this.getNotificationMessages();
  }

  private createNotificationGroup(nameFromDate: string, date: Date, readKeys: string[]): IhmNotificationGroup {
    // Create a new group.
    const group: IhmNotificationGroup = {
      // Set its name.
      name: nameFromDate,
      // Set the relative date for the final user.
      relativeDate: moment(date).calendar(null, {
        lastDay: '[Yesterday]',
        sameDay: '[Today]',
        nextDay: '[Tomorrow]',
        lastWeek: '[Last] dddd',
        nextWeek: 'dddd',
        sameElse: 'L'
      }),
      // Get all messages that share this date.
      messages: _.filter(
        this.notifications,
        (message: IhmNotificationMessage) => moment(message.date).format(this.DATE_FORMAT_FOR_GROUP_NAME) === nameFromDate
      )
    };

    group.messages.forEach((message: IhmNotificationMessage) => {
      message.unread = !readKeys || !readKeys.length || !readKeys.some((key: string) => key === message.key);
    });

    // Order messages by unread and date.
    group.messages = _.orderBy(group.messages, (message: IhmNotificationMessage) => [message.unread, message.date], ['desc', 'desc']);

    return group;
  }

  private getNotificationsCount(): string {
    if (!this.notifications) {
      return null;
    }

    // Get the read notifications from the storage.
    const readKeys: string[] = JSON.parse(this.localStorageService.getItem(this.localStorageService.notificationDrawerReadKeys));
    const unreadKeys: string[] = _.filter(
      this.notifications,
      (notification: IhmNotificationMessage) => _.indexOf(readKeys, notification.key) === -1
    );
    const count: number = unreadKeys.length;

    if (count <= 0) {
      return null;
    }

    if (count > 9) {
      return '9+';
    }

    return count.toString();
  }
}
